import {
  generateVideoThumbnails,
  importFileandPreview,
} from "@rajesh896/video-thumbnails-generator";
import { useEffect, useMemo, useState, useRef } from "react";
import { toast } from "react-toastify";
import type { CoverFrame } from "shared/types/media";
import { convertCustomerUploadsS3ToCloudFront } from "../../../../../libs/media-files";
import { extractMediaUrls } from "../../utils";
import type { LazyBlobRef, UseVideoCoverProps } from "./types";
import {
  clearLazyBlobRef,
  convertVideoUrlToBlob,
  ensureFile,
  revokeBlobUrl,
} from "./utils";
import { logVideoCoverBreadcrumb, withVideoCoverSpan } from "./observability";
import { resolveMediaCorrelationKey } from "utils/media-observability";
import { allowedSelectCoverPlatforms } from "./const";

const selectableCoverPlatforms =
  allowedSelectCoverPlatforms as readonly string[];

function isVideoCoverSelectorEligible({
  isStory,
  isVideoPost,
  mediaUrlCount,
  storyType,
}: {
  isStory?: boolean;
  isVideoPost: boolean;
  mediaUrlCount: number;
  storyType?: string;
}) {
  if (!isVideoPost || mediaUrlCount !== 1) {
    return false;
  }

  if (!isStory) {
    return true;
  }

  return storyType === "reel";
}

function canSelectVideoCoverForSelection({
  selectedPlatform,
  platforms,
}: {
  selectedPlatform: string;
  platforms: UseVideoCoverProps["platforms"];
}) {
  if (selectedPlatform === "LINKED") {
    return platforms.some((platform) =>
      selectableCoverPlatforms.includes(platform.TYPE)
    );
  }

  return selectableCoverPlatforms.includes(selectedPlatform);
}

export const useVideoCover = ({
  platformPost,
  selectedPlatform,
  platforms,
  thumbCount = 10,
}: UseVideoCoverProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [videoMedia, setVideoMedia] = useState<File | null>(null);
  const [videoUrl, setVideoUrl] = useState<string>("");
  const [thumbnails, setThumbnails] = useState<string[]>([]);
  const [videoLoadingError, setVideoLoadingError] = useState(false);

  // Track the current blob URL for cleanup
  const videoUrlRef = useRef<string>("");
  const initIdRef = useRef(0);

  // Ref for lazy blob creation (used by onCoverChanged fallback)
  const lazyBlobRef = useRef<LazyBlobRef["current"]>(null);

  // Sync ref with state and revoke previous URL when it changes
  useEffect(() => {
    const previousUrl = videoUrlRef.current;
    const currentUrl = videoUrl;

    // If URL changed and there was a previous one, revoke it
    if (previousUrl && previousUrl !== currentUrl) {
      revokeBlobUrl(previousUrl);
    }

    // Update ref to track current URL
    videoUrlRef.current = currentUrl;
  }, [videoUrl]);

  // Cleanup on unmount - revoke whatever URL is current
  useEffect(() => {
    return () => {
      revokeBlobUrl(videoUrlRef.current);
      videoUrlRef.current = "";

      clearLazyBlobRef(lazyBlobRef);
    };
  }, []);

  const mediaFiles = useMemo(() => {
    const { mediaFiles, isVideo, url } = platformPost;

    if (Array.isArray(mediaFiles) && mediaFiles.length > 0) {
      return mediaFiles;
    }

    if (isVideo && url) {
      return [
        {
          isVideo,
          url,
        },
      ];
    }

    return [];
  }, [platformPost]);

  const isVideoPost =
    mediaFiles.length === 1 && mediaFiles[0]?.isVideo === true;

  const showVideoSelector = useMemo(() => {
    const { isStory, storyType } = platformPost;
    const extractedMediaUrls = extractMediaUrls(platformPost);
    const mediaUrlCount = extractedMediaUrls?.length ?? mediaFiles.length;

    if (
      !isVideoCoverSelectorEligible({
        isStory,
        isVideoPost,
        mediaUrlCount,
        storyType,
      })
    ) {
      return false;
    }

    return canSelectVideoCoverForSelection({ selectedPlatform, platforms });
  }, [platformPost, selectedPlatform, platforms, isVideoPost]);

  const { url, thumbnailUrl, lowQualityVideo, coverFrames, meta } =
    mediaFiles?.[0] || {};
  const originalMediaUrl = convertCustomerUploadsS3ToCloudFront(url || "");
  const lowQualityMediaUrl = convertCustomerUploadsS3ToCloudFront(
    lowQualityVideo?.url || ""
  );
  const normalizedThumbnailUrl = convertCustomerUploadsS3ToCloudFront(
    thumbnailUrl || ""
  );
  const mediaUrl = lowQualityMediaUrl || originalMediaUrl;

  // Extract video duration from metadata (seconds)
  const videoDurationFromMeta = meta?.duration ?? meta?.format?.duration;

  // Determine if we can use backend-provided cover frames
  const hasCoverFrames = Array.isArray(coverFrames) && coverFrames.length > 0;
  const useCoverFramesFastPath = hasCoverFrames;
  const coverFramesKey = useMemo(
    () =>
      useCoverFramesFastPath
        ? coverFrames
            .map((frame: CoverFrame) => `${frame.timestampMs}:${frame.url}`)
            .join("|")
        : "",
    [coverFrames, useCoverFramesFastPath]
  );

  useEffect(() => {
    if (!mediaUrl || !showVideoSelector) {
      if (videoUrl) {
        setVideoUrl("");
        setThumbnails([]);
        setVideoMedia(null);
        setVideoLoadingError(false);
      }
      return;
    }

    // Reset lazy blob when media changes
    clearLazyBlobRef(lazyBlobRef);

    const observabilityMetadata = {
      mediaUrl,
      originalMediaUrl,
      lowQualityMediaUrl,
      showVideoSelector,
      hasCoverFrames: useCoverFramesFastPath,
      coverFramesCount: coverFrames?.length ?? 0,
      thumbCount,
      selectedPlatform,
      mediaCorrelationKey: resolveMediaCorrelationKey({
        assetId: mediaFiles?.[0]?.asset_id,
        urls: [mediaUrl, originalMediaUrl, lowQualityMediaUrl, thumbnailUrl],
        fileName: mediaFiles?.[0]?.fileName,
      }),
    };

    logVideoCoverBreadcrumb("video_cover.init.start", observabilityMetadata);

    // If backend coverFrames are available, use them directly — skip blob download.
    // Increment initIdRef to invalidate any in-flight legacy async init, preventing
    // a stale blob-generation completion from overwriting the coverFrames state.
    if (useCoverFramesFastPath) {
      ++initIdRef.current;
      logVideoCoverBreadcrumb("video_cover.init.cover_frames_path", {
        ...observabilityMetadata,
      });
      setThumbnails(coverFrames.map((f: CoverFrame) => f.url));
      setVideoUrl(mediaUrl);
      setVideoMedia(null);
      setIsLoading(false);
      setVideoLoadingError(false);
      return;
    }

    // Legacy path: download video as blob and generate thumbnails client-side
    const init = async () => {
      const currentInitId = ++initIdRef.current;
      setIsLoading(true);
      setVideoLoadingError(false);

      try {
        logVideoCoverBreadcrumb("video_cover.init.legacy_path", {
          ...observabilityMetadata,
        });

        await withVideoCoverSpan(
          "video_cover.init",
          {
            ...observabilityMetadata,
            phase: "init",
          },
          async () => {
            const videoBlob = await convertVideoUrlToBlob(mediaUrl);
            if (currentInitId !== initIdRef.current) return;

            const video = ensureFile(videoBlob);
            const res = await importFileandPreview(video);
            if (currentInitId !== initIdRef.current) {
              revokeBlobUrl(res);
              return;
            }

            try {
              logVideoCoverBreadcrumb(
                "video_cover.init.generate_thumbnails.start",
                observabilityMetadata
              );
              const thumbs = await withVideoCoverSpan(
                "video_cover.generate_thumbnails",
                observabilityMetadata,
                async () => generateVideoThumbnails(video, thumbCount, "jpg")
              );
              if (currentInitId !== initIdRef.current) {
                revokeBlobUrl(res);
                return;
              }
              setThumbnails(thumbs);
              logVideoCoverBreadcrumb(
                "video_cover.init.generate_thumbnails.success",
                {
                  ...observabilityMetadata,
                  thumbnailsCount: thumbs.length,
                }
              );
            } catch (error) {
              if (currentInitId !== initIdRef.current) {
                revokeBlobUrl(res);
                return;
              }
              console.error("Error generating video thumbnails:", error);
              logVideoCoverBreadcrumb(
                "video_cover.init.generate_thumbnails.failure",
                {
                  ...observabilityMetadata,
                  errorMessage:
                    error instanceof Error ? error.message : String(error),
                }
              );
              toast.error(
                "Fail to generate video thumbnails for this video, please try again later."
              );
            }

            if (currentInitId !== initIdRef.current) {
              revokeBlobUrl(res);
              return;
            }
            setVideoMedia(video);
            setVideoUrl(res);
            logVideoCoverBreadcrumb("video_cover.init.success", {
              ...observabilityMetadata,
              videoUrlKind: "blob",
            });
          }
        );
      } catch (error) {
        if (currentInitId !== initIdRef.current) return;
        console.error(error);
        setVideoLoadingError(true);
        logVideoCoverBreadcrumb("video_cover.init.failure", {
          ...observabilityMetadata,
          errorMessage: error instanceof Error ? error.message : String(error),
        });
        toast.error(
          "Fail to load the video, please remove the video and try again."
        );
      } finally {
        if (currentInitId === initIdRef.current) {
          setIsLoading(false);
        }
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaUrl, showVideoSelector, useCoverFramesFastPath, coverFramesKey]);

  return {
    showVideoSelector,
    isLoading,
    videoMedia,
    videoUrl,
    thumbnails,
    thumbnailUrl: normalizedThumbnailUrl,
    videoLoadingError,
    originalMediaUrl,
    lowQualityMediaUrl,
    coverFrames: hasCoverFrames ? coverFrames : undefined,
    lazyBlobRef,
    videoDurationFromMeta,
  };
};
